import { createStackNavigator, StackNavigationProp } from '@react-navigation/stack';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { MenuProvider } from 'react-native-popup-menu';

import ChangeLogs from './pages/ChangeLogs';
import ContactUs from './pages/ContactUs';
import FrequentlyAskedQuestions from './pages/FrequentlyAskedQuestions';
import PublicFrequentlyAskedQuestions from './pages/PublicFrequentlyAskedQuestions';
import Login from './pages/Login';
import Menu from './pages/Menu';
import ResetPassword from './pages/ResetPassword';
import TermsOfUse from './pages/TermsOfUse';
import { translate } from './services/translate';

// cameras
import AlertGroups from './pages/cameras/AlertGroups/AlertGroups';
import CameraPlayer from './pages/cameras/CameraPlayer/CameraPlayer';
import CameraDetails from './pages/cameras/Cameras/CameraDetails';
import CamerasList from './pages/cameras/Cameras/CamerasList';
import CamerasMap from './pages/cameras/CamerasMap/CamerasMap';
import CameraTags from './pages/cameras/CameraTags/CameraTags';
import Downloads from './pages/cameras/Downloads/Downloads';
import DownloadTags from './pages/cameras/DownloadTags/DownloadTags';
import Mosaic from './pages/cameras/Mosaic/Mosaic';
import MosaicDetail from './pages/cameras/Mosaic/MosaicDetail';
import VideoAnalysis from './pages/cameras/VideoAnalysis/VideoAnalysis';
import VideoAnalysisDetails from './pages/cameras/VideoAnalysisDetails/index';
import CamerasReports from './pages/cameras/Reports/Reports';

// dispatch
import Assignments from './pages/dispatch/Assignments/Assignment';
import CityProperties from './pages/dispatch/CityProperties/CityProperties';
import CityPropertyForm from './pages/dispatch/CityProperties/CityPropertyForm';
import DispatchOccurrenceDetail from './pages/dispatch/DispatchOccurrenceDetail';
import Equipment from './pages/dispatch/Equipments/Equipment';
import FinishCategories from './pages/dispatch/FinishCategories/index';
import ObjectsCategories from './pages/dispatch/ObjectCategories/ObjectCategory';
import Occurrences from './pages/dispatch/Occurrences/Occurrences';
import OccurrenceTags from './pages/dispatch/OccurrenceTags/OccurrenceTags';
import OccurrenceTypes from './pages/dispatch/OccurrenceTypes/OccurrenceTypes';
import PersonRelations from './pages/dispatch/PersonRelation/PersonRelations';
import PoliceStationForm from './pages/dispatch/PoliceStations/PoliceStationForm';
import PoliceStations from './pages/dispatch/PoliceStations/PoliceStations';
import Providences from './pages/dispatch/Providences/Providences';
import DispatchReports from './pages/dispatch/Reports/Reports';
import DispatchReportTags from './pages/dispatch/ReportTags/ReportTags';
import Resume from './pages/dispatch/Resume/Resume';
import TeamNames from './pages/dispatch/TeamNames/TeamNames';
import TeamName from './pages/dispatch/TeamNames/TeamName';
import TeamPrograms from './pages/dispatch/TeamPrograms/TeamPrograms';
import UnitPauseReasons from './pages/dispatch/UnitPauseReasons/UnitPauseReasons';
import UnitForm from './pages/dispatch/Units/UnitForm';
import Units from './pages/dispatch/Units/Units';
import UnitDisplacedReasons from './pages/dispatch/UnitDisplacedReasons/UnitDisplacedReasons';
import VehicleRelations from './pages/dispatch/VehicleRelation/VehicleRelations';
import HandcuffReasons from './pages/dispatch/HandcuffReasons/HandcuffReasons';
import UnitFinishEarlyReasons from './pages/dispatch/UnitFinishEarlyReasons/UnitFinishEarlyReasons';

// facial
import LiveAlertEventDetail from './pages/facial/LiveAlerts/LiveAlertEventDetail';
import LiveAlerts from './pages/facial/LiveAlerts/LiveAlerts';
import LiveEventDetail from './pages/facial/LiveEvents/LiveEventDetail';
import LiveEvents from './pages/facial/LiveEvents/LiveEvents';
import Markers from './pages/facial/Markers/Markers';
import IgnoredPeople from './pages/facial/IgnoredPeople/IgnoredPeople';
import PeopleList from './pages/facial/People/PeopleList';
import PersonForm from './pages/facial/PersonForm/PersonForm';
import Search from './pages/facial/Search/Search';
import SearchEventDetail from './pages/facial/Search/SearchEventDetail';

// lpr
import LiveAlertLprDetection from './pages/lpr/Detection/LiveAlertLprDetection';
import LiveEventLprDetection from './pages/lpr/Detection/LiveEventLprDetection';
import SearchLprDetection from './pages/lpr/Detection/SearchLprDetection';
import LprLiveAlerts from './pages/lpr/LiveAlerts/LiveAlerts';
import LprLiveEvents from './pages/lpr/LiveEvents/LiveEvents';
import Plates from './pages/lpr/Plates/Plates';
import LprReports from './pages/lpr/Reports/Reports';
import LprSearch from './pages/lpr/Search/Search';

// guardian app
import GuardianAppUsers from './pages/guardian-app/Users';

// access
import ActingBodies from './pages/access/ActingBodies/ActingBodies';
import ActingBodiesForm from './pages/access/ActingBodies/ActingBodyForm';
import ActingBodyUnits from './pages/access/ActingBodyUnits/ActingBodyUnits';
import ActingBodyCommands from './pages/access/ActingBodyCommands/ActingBodyCommands';
import Roles from './pages/access/Roles/Roles';
import AllowedIps from './pages/access/AllowedIps/AllowedIps';
import GroupForm from './pages/access/Groups/GroupForm';
import Groups from './pages/access/Groups/Groups';
import InstallationCompanies from './pages/access/InstallationCompanies/InstallationCompanies';
import InternetCompanies from './pages/access/InternetCompanies/InternetCompanies';
import AbuseReports from './pages/access/AbuseReports/AbuseReports';
import PermissionLevelForm from './pages/access/PermissionLevels/PermissionLevelForm';
import PermissionLevels from './pages/access/PermissionLevels/PermissionLevels';
import PlatformSettings from './pages/access/PlatformSettings/PlatformSettings';
import PtzPriority from './pages/access/PtzPriority/PtzPriority';
import Turnstiles from './pages/access/Turnstiles/Turnstiles';
import Users from './pages/access/Users/Users';
import UserForm from './pages/access/Users/UserForm';
import UserTags from './pages/access/UserTags/UserTags';
import WorkSchedules from './pages/access/WorkSchedules/WorkSchedules';
import Skills from './pages/access/Skills/Skill';
import Holidays from './pages/access/Holidays/Holidays';
import SupportTickets from './pages/access/SupportTickets/SupportTickets';
import SupportTicketDetail from './pages/access/SupportTickets/SupportTicketDetail';

// events
import EventsBoard from './pages/events/EventsBoard';
import EventsOccurrenceDetail from './pages/events/EventsOccurrenceDetail';

// App Agent
import AgentSignatures from './pages/app-agent/AgentSignatures';
import AgentUnits from './pages/app-agent/AgentUnits/AgentUnits';
import CortexFinder from './pages/app-agent/CortexFinder/CortexFinder';
import AgentOccurrenceDetails from './pages/app-agent/Occurrences/AgentOccurrenceDetails';
import AgentOccurrences from './pages/app-agent/Occurrences/AgentOccurrences';
import PersonOfInterest from './pages/app-agent/PersonOfInterest';
import OpenVacancies from './pages/app-agent/Deac/OpenVacancies/OpenVacancies';
import VacancyDetailsApply from './pages/app-agent/Deac/OpenVacancies/VacancyDetailsApply';
import Candidacies from './pages/app-agent/Deac/Candidacies/Candidacies';
import VacancyDetailsCancel from './pages/app-agent/Deac/Candidacies/VacancyDetailsCancel';
import Debugger from './pages/app-agent/Debugger/Debugger';
import Services from './pages/app-agent/Services/Services';
import LprEvents from './pages/app-agent/LprEvents/LprEvents';

// Dashboard
import DashboardAgents from './pages/dashboard/DashboardAgents/DashboardAgents';
import DashboardCameras from './pages/dashboard/DashboardCameras';
import DashboardOccurrences from './pages/dashboard/DashboardOccurrences';
import DashboardVehicles from './pages/dashboard/DashboardVehicles/DashboardVehicles';
import DashboardPlateMonitoring from './pages/dashboard/DashboardPlateMonitoring/DashboardPlateMonitoring';

// Logs
import LogList from './pages/logs/LogList/LogList';
import LogReports from './pages/logs/Reports/Reports';

// Smart Search Facial
import SmartSearchFacial from './pages/smart-search-facial/SmartSearchFacial';

// User
import Account from './pages/user/Account';
import Notes from './pages/user/Notes';
import Password from './pages/user/Password';
import Sessions from './pages/user/Sessions';
import Calendar from './pages/user/Calendar';
import OwnSupportTickets from './pages/user/OwnSupportTickets';
import GetOwnSupportTicket from './pages/user/GetOwnSupportTicket';

// CRM
import AlarmCenterDetails from './pages/crm/AlarmCenterDetails';
import AlarmCentersList from './pages/crm/AlarmCentersList';
import TicketReasons from './pages/crm/TicketReason/TicketReasons';
import CameraHistory from './pages/crm/CameraHistory/index';
import MaintenanceRequests from './pages/crm/MaintenanceRequests/index';
import Management from './pages/crm/Management/Management';
import ManagementDetails from './pages/crm/ManagementDetails/ManagementDetails';
import OfflineEventsKanban from './pages/crm/OfflineEventsKanban';

// Plate Validation
import CloneSuspicions from './pages/cloned-plate/clone-suspicions/CloneSuspicions';
import ClonedPlates from './pages/cloned-plate/cloned-plates/ClonedPlates';

// Signatures
import Signatures from './pages/signatures/Signatures';

// Armory
import ArmoryShifts from './pages/armory/ArmoryShifts/ArmoryShifts';
import ArmoryShiftForm from './pages/armory/ArmoryShifts/ArmoryShiftForm';
import MyArmoryShifts from './pages/armory/MyArmoryShifts/MyArmoryShifts';

// Deac
import VacancyLevels from './pages/deac/VacancyLevels/VacancyLevels';
import Workplaces from './pages/deac/Workplaces/Workplaces';
import VacancyTags from './pages/deac/VacancyTags/VacancyTags';
import VacancyCancellationReasons from './pages/deac/VacancyCancellationReasons/VacancyCancellationReasons';
import Vacancies from './pages/deac/Vacancies/Vacancies';
import VacancyForm from './pages/deac/Vacancies/VacancyForm';
import PresenceControl from './pages/deac/PresenceControl/PresenceControl';
import DeacReports from './pages/deac/Reports/Reports';
import Dashboard from './pages/deac/Dashboard/Dashboard';
import Applications from './pages/deac/Applications/Applications';
import VacanciesTemplates from './pages/deac/VacanciesTemplates/VacanciesTemplates';
import VacancyTemplateForm from './pages/deac/VacanciesTemplates/VacancyTemplateForm';

// Third Party Approved Companies
import Inscriptions from './pages/third-party-approved-companies/Inscriptions/Inscriptions';
import InscriptionForm from './pages/third-party-approved-companies/Inscriptions/InscriptionForm';
import DonatedCamerasList from './pages/third-party-approved-companies/DonatedCameras/DonatedCamerasList';

// Citizen
import CitizenManagement from './pages/citizen/CitizenManagement/CitizenManagement';
import CitizenLprEvents from './pages/citizen/CitizenLprEvents/CitizenLprEvents';
import CitizenLprSearch from './pages/citizen/CitizenLprSearch/CitizenLprSearch';
import CitizenLprEventsDetails from './pages/citizen/CitizenLprEvents/CitizenLprEventsDetails';
import CitizenBlockReasons from './pages/citizen/CitizenBlockReasons/CitizenBlockReasons';

//Param typings
import {
    type WebRtcParamList,
    type AccessParamList,
    type AppAgentParamList,
    type ArmoryParamList,
    type CamerasParamList,
    type ClonedPlateParamList,
    type CRMParamList,
    type DashBoardParamList,
    type DeacParamList,
    type DispatchParamList,
    type EventParamList,
    type FacialParamList,
    type GuardianAppParamList,
    type LogsParamList,
    type LPRParamList,
    type RootStackParamList,
    type SignaturesParamList,
    type SmartSearchFacialParamList,
    type UserParamList,
    type ThirdPartyApprovedCompaniesParamList,
    CitizenParamList,
    StreamServersParamList,
} from './typings/Params';

import { DefaultTheme, NavigationContainer, NavigationContainerRefWithCurrent, useNavigation } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import DefaultPageContainer from './components/DefaultPageContainer';
import DefaultPageLayout from './components/DefaultPageLayout';
import { ActivityIndicator, View } from 'react-native';
import Publisher from './pages/webrtc/Publisher';
import Consumer from './pages/webrtc/Consumer';
import { useCurrentApp } from './context/CurrentApp';
import TicketDetail from './pages/crm/TicketDetail/TicketDetail';
import zod from 'zod';
import { AssignmentsSchema, CityPropertiesSchema, CityPropertyFormSchema, DispatchOccurrenceDetailSchema, DispatchReportsSchema, EquipmentsSchema, FinishCategoriesSchema, HandcuffReasonsSchema, ObjectsCategoriesSchema, OccurrencesSchema, OccurrenceTagsSchema, OccurrenceTypesSchema, PersonRelationsSchema, PoliceStationFormSchema, PoliceStationsSchema, ProvidencesSchema, ReportTagsSchema, TeamNameSchema, TeamNamesSchema, TeamProgramsSchema, UnitDisplacedReasonsSchema, UnitFinishEarlyReasonsSchema, UnitFormSchema, UnitPauseReasonsSchema, UnitsSchema, VehicleRelationsSchema } from './schemas/dispatch';
import { AlertGroupsSchema, CameraDetailsSchema, CamerasListSchema, CamerasReportsSchema, CameraTagsSchema, DownloadsSchema, DownloadTagsSchema, MosaicDetailSchema, MosaicSchema, VideoAnalysisDetailsSchema, VideoAnalysisSchema } from './schemas/cameras';
import { EventsBoardSchema, EventsOccurrenceDetailSchema } from './schemas/events';
import { CitizenManagementSchema, CitizenBlockReasonsSchema, CitizenLprEventsSchema, CitizenLprSearchSchema } from './schemas/citizen';
import { useStyles } from 'react-native-unistyles';
import { ManagementSchema } from './schemas/crm';
import { LprLiveAlertsSchema, LprLiveEventsSchema, LprReportsSchema, LprSearchSchema, PlatesSchema } from './schemas/lpr';
import { AbuseReportsSchema, ActingBodiesSchema, ActingBodyCommandsSchema, ActingBodyFormSchema, ActingBodyUnitsSchema, AllowedIpsSchema, GroupFormSchema, GroupsSchema, HolidaysSchema, InstallationCompaniesSchema, InternetCompaniesSchema, PermissionLevelFormSchema, PermissionLevelsSchema, PlatformSettingsSchema, RolesSchema, SkillsSchema, SupportTicketDetailSchema, SupportTicketsSchema, TurnstilesSchema, UserFormSchema, UsersSchema, UserTagsSchema, WorkSchedulesSchema } from './schemas/access';
import { DonatedCamerasListSchema, InscriptionFormSchema, InscriptionsManagementSchema } from './schemas/third-party-approved-companies';
import StreamServers from './pages/stream-servers/ServerFarms';
import { ServerFarmDetailSchema } from './schemas/stream-servers';
import ServerFarmDetail from './pages/stream-servers/ServerFarmDetail';
import { CameraMapPlayerSchema, CameraPlayerSchema, ManagementDetailsSchema } from './schemas/root';

const Stack = createStackNavigator<RootStackParamList>();
const DispatchStack = createStackNavigator<DispatchParamList>(); // Create a separate stack for 'Dispatch' and its sub-pages
const CamerasStack = createStackNavigator<CamerasParamList>(); // Create a separate stack for 'CameraMap' and its sub-pages
const WebRtcStack = createStackNavigator<WebRtcParamList>();
const StreamServersStack = createStackNavigator<StreamServersParamList>();
const FacialStack = createStackNavigator<FacialParamList>(); // Create a separate stack for 'FacialStack' and its sub-pages
const LprStack = createStackNavigator<LPRParamList>(); // Create a separate stack for 'LprStack' and its sub-pages
const GuardianAppStack = createStackNavigator<GuardianAppParamList>(); // Create a separate stack for 'AccessStack' and its sub-pages
const AccessStack = createStackNavigator<AccessParamList>(); // Create a separate stack for 'AccessStack' and its sub-pages
const EventStack = createStackNavigator<EventParamList>(); // Create a separate stack for 'EventStack' and its sub-pages
const AppAgentStack = createStackNavigator<AppAgentParamList>(); // Create a separate stack for 'AppAgent' and its sub-pages
const DashboardStack = createStackNavigator<DashBoardParamList>(); // Create a separate stack for 'DashboardStack' and its sub-pages
const LogsStack = createStackNavigator<LogsParamList>(); // Create a separate stack for 'LogsStack' and its sub-pages
const SmartSearchFacialStack = createStackNavigator<SmartSearchFacialParamList>(); // Create a separate stack for 'SmartSearchFacialStack' and its sub-pages
const CrmStack = createStackNavigator<CRMParamList>(); // Create a separate stack for 'CrmStack' and its sub-pages
const UserStack = createStackNavigator<UserParamList>(); // Create a separate stack for 'UserStack' and its sub-pages
const ClonedPlateStack = createStackNavigator<ClonedPlateParamList>(); // Create a separate stack for 'ClonedPlateStack' and its sub-pages
const SignaturesStack = createStackNavigator<SignaturesParamList>(); // Create a separate stack for 'Signatures' and its sub-pages
const ArmoryStack = createStackNavigator<ArmoryParamList>(); // Create a separate stack for 'Armory' and its sub-pages
const DeacStack = createStackNavigator<DeacParamList>(); // Create a separate stack for 'DEAC' and its sub-pages
const ThirdPartyApprovedCompaniesStack = createStackNavigator<ThirdPartyApprovedCompaniesParamList>(); // Create a separate stack for 'Third Party Approved Companies' and its sub-pages
const CitizenStack = createStackNavigator<CitizenParamList>(); // Create a separate stack for 'Citizen' and its sub-pages

interface Screens {
    [pageName: string]: {
        path: string;
        screens?: Screens;
        module?: string;
        selectedItem?: string;
        parse?: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            [queryParam: string]: any;
        };
    };
}

interface RouteConfig {
    screens: Screens;
}

function createLinkingParse<T extends zod.ZodRawShape>(
    schema: zod.ZodObject<T> | zod.ZodOptional<zod.ZodObject<T>>
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parse: Record<string, (value: string) => any> = {};

    if (schema instanceof zod.ZodOptional) {
        return parse;
    }

    const shape = schema.shape;

    for (const key in shape) {
        if (Object.hasOwn(shape, key)) {
            parse[key] = (value: string) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const result = schema.pick({ [key]: true }).safeParse({
                    [key]: value,
                });

                if (!result.success) {
                    return undefined;
                }

                return result.data[key];
            };
        }
    }

    return parse as {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [K in keyof zod.infer<typeof schema>]: (value: string) => zod.infer<typeof schema>[K];
    };
}

const config: RouteConfig = {
    screens: {
        MainPage: {
            path: ''
        },
        TermsOfUse: {
            path: '/terms-of-use'
        },
        ChangeLogs: {
            path: '/change-logs',
        },
        ContactUs: {
            path: '/contact-us',
        },
        FrequentlyAskedQuestions: {
            path: '/frequently-asked-questions',
        },
        PublicFrequentlyAskedQuestions: {
            path: '/public-frequently-asked-questions',
        },
        Login: {
            path: '/login',
        },
        Menu: {
            path: '/menu',
        },
        ResetPassword: {
            path: '/reset-password/:token'
        },
        Dispatch: {
            path: '/dispatch',
            screens: {
                Resume: {
                    path: 'resume',
                    module: 'dispatch',
                    selectedItem: 'resume',
                },
                TeamNames: {
                    path: 'team-names',
                    module: 'dispatch',
                    selectedItem: 'team-names',
                    parse: createLinkingParse(TeamNamesSchema),
                },
                TeamName: {
                    path: 'team-names/form/:id?',
                    module: 'dispatch',
                    selectedItem: 'team-names',
                    parse: createLinkingParse(TeamNameSchema),
                },
                TeamPrograms: {
                    path: 'team-programs',
                    module: 'dispatch',
                    selectedItem: 'team-programs',
                    parse: createLinkingParse(TeamProgramsSchema),
                },
                Units: {
                    path: 'units',
                    module: 'dispatch',
                    selectedItem: 'units',
                    parse: createLinkingParse(UnitsSchema),
                },
                UnitForm: {
                    path: 'units/form/:id?',
                    module: 'dispatch',
                    selectedItem: 'units',
                    parse: createLinkingParse(UnitFormSchema),
                },
                ObjectsCategories: {
                    path: 'objects-categories',
                    module: 'dispatch',
                    selectedItem: 'objects-categories',
                    parse: createLinkingParse(ObjectsCategoriesSchema),
                },
                FinishCategories: {
                    path: 'finish-categories',
                    module: 'dispatch',
                    selectedItem: 'finish-categories',
                    parse: createLinkingParse(FinishCategoriesSchema),
                },
                PoliceStations: {
                    path: 'police-stations',
                    module: 'dispatch',
                    selectedItem: 'police-stations',
                    parse: createLinkingParse(PoliceStationsSchema),
                },
                CityProperties: {
                    path: 'city-properties',
                    module: 'dispatch',
                    selectedItem: 'city-properties',
                    parse: createLinkingParse(CityPropertiesSchema),
                },
                CityPropertyForm: {
                    path: 'city-properties/form/:id?',
                    module: 'dispatch',
                    selectedItem: 'city-properties',
                    parse: createLinkingParse(CityPropertyFormSchema),
                },
                HandcuffReasons: {
                    path: 'handcuff-reasons',
                    module: 'dispatch',
                    selectedItem: 'handcuff-reasons',
                    parse: createLinkingParse(HandcuffReasonsSchema),
                },
                PoliceStationForm: {
                    path: 'police-stations/form/:id?',
                    module: 'dispatch',
                    selectedItem: 'police-stations',
                    parse: createLinkingParse(PoliceStationFormSchema),
                },
                UnitPauseReasons: {
                    path: 'unit-pause-reasons',
                    module: 'dispatch',
                    selectedItem: 'unit-pause-reasons',
                    parse: createLinkingParse(UnitPauseReasonsSchema),
                },
                UnitFinishEarlyReasons: {
                    path: 'unit-finish-early-reasons',
                    module: 'dispatch',
                    selectedItem: 'unit-finish-early-reasons',
                    parse: createLinkingParse(UnitFinishEarlyReasonsSchema),
                },
                UnitDisplacedReasons: {
                    path: 'unit-displaced-reasons',
                    module: 'dispatch',
                    selectedItem: 'unit-displaced-reasons',
                    parse: createLinkingParse(UnitDisplacedReasonsSchema)
                },
                Occurrences: {
                    path: 'occurrences',
                    module: 'dispatch',
                    selectedItem: 'occurrences',
                    parse: createLinkingParse(OccurrencesSchema),
                },
                DispatchOccurrenceDetail: {
                    path: 'occurrences/detail/:occurrenceId?',
                    module: 'dispatch',
                    selectedItem: 'occurrences',
                    parse: createLinkingParse(DispatchOccurrenceDetailSchema),
                },
                OccurrenceTypes: {
                    path: 'occurrence-types',
                    module: 'dispatch',
                    selectedItem: 'occurrence-types',
                    parse: createLinkingParse(OccurrenceTypesSchema),
                },
                Equipments: {
                    path: 'equipments',
                    module: 'dispatch',
                    selectedItem: 'equipments',
                    parse: createLinkingParse(EquipmentsSchema),
                },
                VehicleRelations: {
                    path: 'vehicle-relations',
                    module: 'dispatch',
                    selectedItem: 'vehicle-relations',
                    parse: createLinkingParse(VehicleRelationsSchema),
                },
                PersonRelations: {
                    path: 'person-relations',
                    module: 'dispatch',
                    selectedItem: 'person-relations',
                    parse: createLinkingParse(PersonRelationsSchema),
                },
                Providences: {
                    path: 'providences',
                    module: 'dispatch',
                    selectedItem: 'providences',
                    parse: createLinkingParse(ProvidencesSchema),
                },
                DispatchReports: {
                    path: 'reports',
                    module: 'dispatch',
                    selectedItem: 'reports',
                    parse: createLinkingParse(DispatchReportsSchema),
                },
                Assignments: {
                    path: 'assignments',
                    module: 'dispatch',
                    selectedItem: 'assignments',
                    parse: createLinkingParse(AssignmentsSchema),
                },
                OccurrenceTags: {
                    path: 'occurrence-tags',
                    module: 'dispatch',
                    selectedItem: 'occurrence-tags',
                    parse: createLinkingParse(OccurrenceTagsSchema),
                },
                ReportTags: {
                    path: 'report-tags',
                    module: 'dispatch',
                    selectedItem: 'report-tags',
                    parse: createLinkingParse(ReportTagsSchema),
                },
            },
        },
        CameraPlayer: {
            path: '/cameras/cameras/watch/:id',
            module: 'cameras',
            selectedItem: 'cameras',
            parse: createLinkingParse(CameraPlayerSchema),
        },
        CameraMapPlayer: {
            path: '/cameras/map/:id',
            module: 'cameras',
            selectedItem: 'map',
            parse: createLinkingParse(CameraMapPlayerSchema),
        },
        WebRTC: {
            path: '/webrtc',
            screens: {
                Publisher: {
                    path: 'publisher',
                    module: 'webrtc',
                    selectedItem: 'publisher',
                },
                Consumer: {
                    path: 'consumer',
                    module: 'webrtc',
                    selectedItem: 'consumer',
                },
            }
        },
        StreamServers: {
            path: '/stream-servers',
            screens: {
                ServerFarms: {
                    path: 'server-farms',
                    module: 'stream-servers',
                    selectedItem: 'server-farms',
                },
                ServerFarmDetail: {
                    path: 'server-farm/:id',
                    module: 'stream-servers',
                    selectedItem: 'server-farms',
                    parse: createLinkingParse(ServerFarmDetailSchema),
                }
            }
        },
        Cameras: {
            path: '/cameras',
            screens: {
                CamerasMap: {
                    path: 'map',
                    module: 'cameras',
                    selectedItem: 'map',
                },
                CamerasList: {
                    path: 'cameras',
                    module: 'cameras',
                    selectedItem: 'cameras',
                    parse: createLinkingParse(CamerasListSchema),
                },
                CameraDetails: {
                    path: 'cameras/details/:id?',
                    module: 'cameras',
                    selectedItem: 'cameras',
                    parse: createLinkingParse(CameraDetailsSchema),
                },
                Mosaic: {
                    path: 'mosaic',
                    module: 'cameras',
                    selectedItem: 'mosaic',
                    parse: createLinkingParse(MosaicSchema),
                },
                MosaicDetail: {
                    path: 'mosaic/:id',
                    module: 'cameras',
                    selectedItem: 'mosaic',
                    parse: createLinkingParse(MosaicDetailSchema),
                },
                CameraTags: {
                    path: 'camera-tags',
                    module: 'cameras',
                    selectedItem: 'camera-tags',
                    parse: createLinkingParse(CameraTagsSchema),
                },
                Downloads: {
                    path: 'downloads',
                    module: 'cameras',
                    selectedItem: 'downloads',
                    parse: createLinkingParse(DownloadsSchema),
                },
                DownloadTags: {
                    path: 'download-tags',
                    module: 'cameras',
                    selectedItem: 'download-tags',
                    parse: createLinkingParse(DownloadTagsSchema),
                },
                VideoAnalysis: {
                    path: 'video-analysis',
                    module: 'cameras',
                    selectedItem: 'video-analysis',
                    parse: createLinkingParse(VideoAnalysisSchema),
                },
                VideoAnalysisDetails: {
                    path: 'video-analysis/details/:id',
                    module: 'cameras',
                    selectedItem: 'video-analysis',
                    parse: createLinkingParse(VideoAnalysisDetailsSchema),
                },
                AlertGroups: {
                    path: 'alert-groups',
                    module: 'cameras',
                    selectedItem: 'alert-groups',
                    parse: createLinkingParse(AlertGroupsSchema),
                },
                CamerasReports: {
                    path: 'cameras-reports',
                    module: 'cameras',
                    selectedItem: 'cameras-reports',
                    parse: createLinkingParse(CamerasReportsSchema),
                }
            },
        },
        LiveEventDetail: {
            path: '/facial/live-events/event/:eventId',
            module: 'facial',
            selectedItem: 'live-events',
        },
        LiveAlertEventDetail: {
            path: '/facial/live-alerts/event/:eventId',
            module: 'facial',
            selectedItem: 'live-alerts',
        },
        SearchEventDetail: {
            path: '/facial/search/event/:eventId',
            module: 'facial',
            selectedItem: 'search',
        },
        Facial: {
            path: '/facial',
            screens: {
                LiveEvents: {
                    path: 'live-events',
                    module: 'facial',
                    selectedItem: 'live-events',
                    parse: {
                        cameras: (cameras: string) => cameras ? cameras.split(',') : [],
                    }
                },
                LiveAlerts: {
                    path: 'live-alerts',
                    module: 'facial',
                    selectedItem: 'live-alerts',
                    parse: {
                        cameras: (cameras: string) => cameras ? cameras.split(',') : [],
                    }
                },
                Search: {
                    path: 'search',
                    module: 'facial',
                    selectedItem: 'search',
                    parse: {
                        onlyWithPerson: (onlyWithPerson: string) => onlyWithPerson === 'true',
                        personId: Number,
                        beginIn: Number,
                        endIn: Number,
                        confidence: Number,
                        cameras: (cameras: string) => cameras ? cameras.split(',') : [],
                        markers: (markers: string) => {
                            return markers.length > 0 ? markers.split(',').map(m => +m) : [];
                        },
                    }
                },
                Markers: {
                    path: 'markers',
                    module: 'facial',
                    selectedItem: 'markers',
                    parse: {
                        page: Number,
                        limit: Number,
                        textFilter: String,
                    },
                },
                IgnoredPeople: {
                    path: 'ignored-people',
                    module: 'facial',
                    selectedItem: 'ignored-people',
                    parse: {
                        page: Number,
                        limit: Number,
                        textFilter: String,
                    },
                },
                PeopleList: {
                    path: 'people',
                    module: 'facial',
                    selectedItem: 'people',
                    parse: {
                        page: Number,
                        onlyActive: (active: string) => active === 'true',
                        markers: (markers: string) => {
                            return markers.length > 0 ? markers.split(',').map(m => +m) : [];
                        },
                    }
                },
                PersonForm: {
                    path: '/people/form/:id?',
                    module: 'facial',
                    selectedItem: 'people',
                },
            }
        },
        SmartSearchFacial: {
            path: '/smart-search-facial',
            screens: {
                SmartSearchFacial: {
                    path: 'search',
                    module: 'smart-search-facial',
                    selectedItem: 'search',
                },
            }
        },
        User: {
            path: '/user',
            screens: {
                Account: {
                    path: 'account',
                    module: 'user',
                    selectedItem: 'account',
                },
                Password: {
                    path: 'password',
                    module: 'user',
                    selectedItem: 'password',
                },
                Sessions: {
                    path: 'sessions',
                    module: 'user',
                    selectedItem: 'sessions',
                },
                Calendar: {
                    path: 'calendar',
                    module: 'user',
                    selectedItem: 'calendar',
                },
                OwnSupportTickets: {
                    path: 'support-tickets',
                    module: 'user',
                    selectedItem: 'support-tickets',
                    parse: {
                        page: Number,
                        limit: Number,
                    }
                },
                GetOwnSupportTicket: {
                    path: '/support-ticket/:id?',
                    module: 'user',
                    selectedItem: 'support-tickets',
                },
                Notes: {
                    path: 'notes',
                    module: 'user',
                    selectedItem: 'notes',
                },
            }
        },
        SearchLprDetection: {
            path: 'LPR/search/lpr-detection/:detectionId',
            module: 'LPR',
            selectedItem: 'search',
        },
        LiveEventLprDetection: {
            path: 'LPR/live-events/lpr-detection/:detectionId',
            module: 'LPR',
            selectedItem: 'live-events',
        },
        LiveAlertLprDetection: {
            path: 'LPR/live-alerts/lpr-detection/:detectionId',
            module: 'LPR',
            selectedItem: 'live-alerts',
        },
        LPR: {
            path: '/LPR',
            screens: {
                LprLiveEvents: {
                    path: 'live-events',
                    module: 'LPR',
                    selectedItem: 'live-events',
                    parse: createLinkingParse(LprLiveEventsSchema),
                },
                LprLiveAlerts: {
                    path: 'live-alerts',
                    module: 'LPR',
                    selectedItem: 'live-alerts',
                    parse: createLinkingParse(LprLiveAlertsSchema),
                },
                LprSearch: {
                    path: 'search',
                    module: 'LPR',
                    selectedItem: 'search',
                    parse: createLinkingParse(LprSearchSchema),
                },
                Plates: {
                    path: 'plates',
                    module: 'LPR',
                    selectedItem: 'plates',
                    parse: createLinkingParse(PlatesSchema),
                },
                LprReports: {
                    path: 'reports',
                    module: 'LPR',
                    selectedItem: 'reports',
                    parse: createLinkingParse(LprReportsSchema),
                },
            }
        },
        ManagementDetails: {
            path: '/CRM/cameras/details/:id?',
            module: 'CRM',
            selectedItem: 'cameras',
            parse: createLinkingParse(ManagementDetailsSchema),
        },
        AlarmCenterDetails: {
            path: '/CRM/alarm-centers/details/:id?',
            module: 'CRM',
            selectedItem: 'alarm-centers',
        },
        CRM: {
            path: '/CRM',
            screens: {
                Management: {
                    path: 'cameras',
                    module: 'CRM',
                    selectedItem: 'cameras',
                    parse: createLinkingParse(ManagementSchema),
                },
                CameraHistory: {
                    path: 'cameras/history/:id',
                    module: 'CRM',
                    selectedItem: 'cameras',
                    parse: {
                        page: Number,
                        limit: Number,
                        onlyOffline: (onlyOffline: string) => onlyOffline === 'true',
                        toDuration: Number,
                        fromDuration: Number,
                        fromStart: Number,
                        toStart: Number,
                        fromFinish: Number,
                        toFinish: Number,
                        polygon: Number,
                    }
                },
                AlarmCentersList: {
                    path: 'alarm-centers',
                    module: 'CRM',
                    selectedItem: 'alarm-centers',
                },
                TicketReasons: {
                    path: 'ticket-reasons',
                    module: 'CRM',
                    selectedItem: 'ticket-reasons',
                    parse: {
                        page: Number,
                        limit: Number,
                        textFilter: String,
                    }
                },
                OfflineEvents: {
                    path: 'cameras/history',
                    module: 'CRM',
                    selectedItem: 'offline-events',
                    parse: {
                        page: Number,
                        onlyOffline: (onlyOffline: string) => onlyOffline === 'true',
                        textFilter: String,
                        toDuration: Number,
                        fromDuration: Number,
                        fromStart: Number,
                        toStart: Number,
                        fromFinish: Number,
                        toFinish: Number,
                        polygon: Number,
                    }
                },
                TicketCameraDetail: {
                    path: 'offline-ticket/camera/:id',
                    module: 'CRM',
                    selectedItem: 'cameras',
                },
                TicketDetail: {
                    path: 'offline-ticket/:id',
                    module: 'CRM',
                    selectedItem: 'offline-events',
                },
                KanbanTicketDetail: {
                    path: 'offline-events-kanban/:id',
                    module: 'CRM',
                    selectedItem: 'offline-events-kanban',
                },
                OfflineEventsKanban: {
                    path: 'offline-events-kanban',
                    module: 'CRM',
                    selectedItem: 'offline-events-kanban',
                    parse: {
                        companyId: Number,
                    },
                },
                MaintenanceRequests: {
                    path: 'maintenance-requests',
                    module: 'CRM',
                    selectedItem: 'maintenance-requests',
                },
            }
        },
        GuardianApp: {
            path: '/guardian-app',
            screens: {
                GuardianAppUsers: {
                    path: 'users',
                    module: 'guardian-app',
                    selectedItem: 'users',
                    parse: {
                        page: Number,
                    }
                },
            }
        },
        ClonedPlate: {
            path: '/cloned-plate',
            screens: {
                CloneSuspicions: {
                    path: 'clone-suspicions',
                    module: 'cloned-plate',
                    selectedItem: 'clone-suspicions',
                    parse: {
                        page: Number,
                    }
                },
                ClonedPlates: {
                    path: 'cloned-plates',
                    module: 'cloned-plate',
                    selectedItem: 'cloned-plates',
                    parse: {
                        page: Number,
                    }
                },
            }
        },
        Access: {
            path: '/access',
            screens: {
                Users: {
                    path: 'users',
                    module: 'access',
                    selectedItem: 'users',
                    parse: createLinkingParse(UsersSchema),
                },
                UserForm: {
                    path: 'users/form/:id?',
                    module: 'access',
                    selectedItem: 'users',
                    parse: createLinkingParse(UserFormSchema),
                },
                PermissionLevels: {
                    path: 'permission-levels',
                    module: 'access',
                    selectedItem: 'permission-levels',
                    parse: createLinkingParse(PermissionLevelsSchema),
                },
                PermissionLevelForm: {
                    path: 'permission-levels/form/:id?',
                    module: 'access',
                    selectedItem: 'permission-levels',
                    parse: createLinkingParse(PermissionLevelFormSchema),
                },
                Turnstiles: {
                    path: 'turnstiles',
                    module: 'access',
                    selectedItem: 'turnstiles',
                    parse: createLinkingParse(TurnstilesSchema),
                },
                Groups: {
                    path: 'groups',
                    module: 'access',
                    selectedItem: 'groups',
                    parse: createLinkingParse(GroupsSchema),
                },
                GroupForm: {
                    path: 'groups/form/:id?',
                    module: 'access',
                    selectedItem: 'groups',
                    parse: createLinkingParse(GroupFormSchema),
                },
                InstallationCompanies: {
                    path: 'installation-companies',
                    module: 'access',
                    selectedItem: 'installation-companies',
                    parse: createLinkingParse(InstallationCompaniesSchema),
                },
                InternetCompanies: {
                    path: 'internet-companies',
                    module: 'access',
                    selectedItem: 'internet-companies',
                    parse: createLinkingParse(InternetCompaniesSchema),
                },
                ActingBodies: {
                    path: 'acting-bodies',
                    module: 'access',
                    selectedItem: 'acting-bodies',
                    parse: createLinkingParse(ActingBodiesSchema),
                },
                ActingBodiesForm: {
                    path: 'acting-bodies/form/:id?',
                    module: 'access',
                    selectedItem: 'acting-bodies',
                    parse: createLinkingParse(ActingBodyFormSchema),
                },
                ActingBodyUnits: {
                    path: 'acting-body-units',
                    module: 'access',
                    selectedItem: 'acting-body-units',
                    parse: createLinkingParse(ActingBodyUnitsSchema),
                },
                ActingBodyCommands: {
                    path: 'acting-body-commands',
                    module: 'access',
                    selectedItem: 'acting-body-commands',
                    parse: createLinkingParse(ActingBodyCommandsSchema),
                },
                Roles: {
                    path: 'roles',
                    module: 'access',
                    selectedItem: 'roles',
                    parse: createLinkingParse(RolesSchema),
                },
                PlatformSettings: {
                    path: 'platform-settings',
                    module: 'access',
                    selectedItem: 'platform-settings',
                    parse: createLinkingParse(PlatformSettingsSchema),
                },
                UserTags: {
                    path: 'user-tags',
                    module: 'access',
                    selectedItem: 'user-tags',
                    parse: createLinkingParse(UserTagsSchema),
                },
                PtzPriority: {
                    path: 'ptz-priority',
                    module: 'access',
                    selectedItem: 'ptz-priority',
                },
                AllowedIps: {
                    path: 'allowed-ips',
                    module: 'access',
                    selectedItem: 'allowed-ips',
                    parse: createLinkingParse(AllowedIpsSchema),
                },
                AbuseReports: {
                    path: 'abuse-reports',
                    module: 'access',
                    selectedItem: 'abuse-reports',
                    parse: createLinkingParse(AbuseReportsSchema),
                },
                WorkSchedules: {
                    path: 'work-schedules',
                    module: 'access',
                    selectedItem: 'work-schedules',
                    parse: createLinkingParse(WorkSchedulesSchema),
                },
                Skills: {
                    path: 'skills',
                    module: 'access',
                    selectedItem: 'skills',
                    parse: createLinkingParse(SkillsSchema),
                },
                Holidays: {
                    path: 'holidays',
                    module: 'access',
                    selectedItem: 'holidays',
                    parse: createLinkingParse(HolidaysSchema),
                },
                SupportTickets: {
                    path: 'support-tickets',
                    module: 'access',
                    selectedItem: 'support-tickets',
                    parse: createLinkingParse(SupportTicketsSchema),
                },
                SupportTicketDetail: {
                    path: 'support-tickets/:id',
                    module: 'access',
                    selectedItem: 'support-tickets',
                    parse: createLinkingParse(SupportTicketDetailSchema),
                }
            }
        },
        Logs: {
            path: '/logs',
            screens: {
                LogList: {
                    path: 'search',
                    module: 'logs',
                    selectedItem: 'search',
                    parse: {
                        userId: Number,
                        sort: (sort: string) => sort === 'asc' ? sort : 'desc',
                        dateGte: Number,
                        dateLte: Number,
                        page: Number,
                        limit: Number,
                    }
                },
                LogReports: {
                    path: 'reports',
                    module: 'logs',
                    selectedItem: 'reports',
                    parse: {
                        page: Number,
                    }
                },
            }
        },
        Events: {
            path: '/events',
            screens: {
                EventsBoard: {
                    path: 'events',
                    module: 'events',
                    selectedItem: 'events',
                    parse: createLinkingParse(EventsBoardSchema),
                },
                EventsOccurrenceDetail: {
                    path: 'events/detail/:occurrenceId',
                    module: 'events',
                    selectedItem: 'events',
                    parse: createLinkingParse(EventsOccurrenceDetailSchema),
                },
            }
        },
        AppAgent: {
            path: '/app-agent',
            screens: {
                PersonOfInterest: {
                    path: 'person-of-interest',
                    module: 'app-agent',
                    selectedItem: 'person-of-interest',
                },
                AgentOccurrences: {
                    path: 'occurrences',
                    module: 'app-agent',
                    selectedItem: 'occurrences',
                },
                AgentOccurrenceDetails: {
                    path: 'occurrences/:occurrenceId',
                    module: 'app-agent',
                    selectedItem: 'occurrences',
                    parse: {
                        occurrenceId: Number,
                    }
                },
                AgentUnits: {
                    path: 'units',
                    module: 'app-agent',
                    selectedItem: 'units',
                },
                CortexFinder: {
                    path: 'cortex-finder',
                    module: 'app-agent',
                    selectedItem: 'cortex-finder',
                },
                AgentSignatures: {
                    path: 'signatures',
                    module: 'app-agent',
                    selectedItem: 'signatures'
                },
                OpenVacancies: {
                    path: 'open-vacancies',
                    module: 'app-agent',
                    selectedItem: 'open-vacancies',
                    parse: {
                        tagsIds: (tags: string) => {
                            return tags ? tags.split(',').map(t => Number(t)) : [];
                        },
                        actingBodyUnitId: Number,
                        actingBodyCommandId: Number,
                        workplaceId: Number,
                        beginIn: Number,
                        endIn: Number,
                        showAvailableVacancies: (showAvailableVacancies: string) => showAvailableVacancies === 'true',
                    }
                },
                VacancyDetailsApply: {
                    path: 'vacancy-details/apply/:id?',
                    module: 'app-agent',
                    selectedItem: 'open-vacancies'
                },
                Candidacies: {
                    path: 'my-candidacies',
                    module: 'app-agent',
                    selectedItem: 'my-candidacies'
                },
                VacancyDetailsCancel: {
                    path: 'vacancy-details/cancel/:id?',
                    module: 'app-agent',
                    selectedItem: 'my-candidacies'
                },
                Debugger: {
                    path: 'debugger',
                    module: 'app-agent',
                    selectedItem: 'debugger',
                    parse: {
                        tags: (tags: string) => {
                            return tags ? tags.split(',').map(t => t) : [];
                        },
                        subTags: (tags: string) => {
                            return tags ? tags.split(',').map(t => t) : [];
                        },
                    }
                },
                Services: {
                    path: 'services',
                    module: 'app-agent',
                    selectedItem: 'services',
                },
                LprEvents: {
                    path: 'lpr-events',
                    module: 'app-agent',
                    selectedItem: 'lpr-events',
                }
            }
        },
        Dashboard: {
            path: '/dashboard',
            screens: {
                DashboardCameras: {
                    module: 'dashboard',
                    selectedItem: 'cameras',
                    path: 'cameras',
                },
                DashboardOccurrences: {
                    module: 'dashboard',
                    selectedItem: 'occurrences',
                    path: 'occurrences',
                    parse: {
                        page: Number,
                        beginIn: Number,
                        endIn: Number,
                        mapPolygonIds: (mapPolygonIds: string) => {
                            return mapPolygonIds.length > 0 ? mapPolygonIds.split(',').map(t => +t) : [];
                        },
                        tags: (tags: string) => {
                            return tags.length > 0 ? tags.split(',').map(t => +t) : [];
                        },
                        occurrenceTypeId: (occurrenceTypeId: string) => {
                            return occurrenceTypeId.length > 0 ? occurrenceTypeId.split(',').map(t => +t) : [];
                        },
                        unitNamesIds: (unitNamesIds: string) => {
                            return unitNamesIds.length > 0 ? unitNamesIds.split(',').map(t => +t) : [];
                        },
                        displaceReasonIds: (displaceReasonIds: string) => {
                            return displaceReasonIds.length > 0 ? displaceReasonIds.split(',').map(t => +t) : [];
                        },
                        triggerTypeIds: (triggerTypeIds: string) => {
                            return triggerTypeIds.length > 0 ? triggerTypeIds.split(',') : [];
                        },
                        latitude: Number,
                        longitude: Number,
                        radius: Number,
                    }
                },
                DashboardAgents: {
                    module: 'dashboard',
                    selectedItem: 'agents',
                    path: 'agents',
                    parse: {
                        unitNamesIds: (unitNamesIds: string) => {
                            return unitNamesIds.length > 0 ? unitNamesIds.split(',').map(t => +t) : [];
                        },
                        actingBodyId: Number,
                        actingBodyUnitId: Number
                    }
                },
                DashboardVehicles: {
                    module: 'dashboard',
                    selectedItem: 'vehicles',
                    path: 'vehicles',
                    parse: {
                        textFilter: String,
                        actingBodyId: Number,
                        actingBodyCommandIds: (actingBodyCommandIds: string) => {
                            return actingBodyCommandIds.length > 0 ? actingBodyCommandIds.split(',').map(t => +t) : [];
                        },
                        categoryList: (categoryList: string) => {
                            return categoryList.length > 0 ? categoryList.split(',') : [];
                        },
                    }
                },
                DashboardPlateMonitoring: {
                    module: 'dashboard',
                    selectedItem: 'plate-monitoring',
                    path: 'plate-monitoring',
                    parse: {
                        groupId: Number,
                        fixedPeriod: String,
                        beginIn: Number,
                        endIn: Number,
                        selectedTab: String,
                    }
                }
            }
        },
        Signatures: {
            path: '/signatures',
            screens: {
                Signatures: {
                    path: 'signatures',
                    module: 'signatures',
                    selectedItem: 'signatures',
                }
            },
        },
        Armory: {
            path: '/armory',
            screens: {
                ArmoryShifts: {
                    path: 'armory-shifts',
                    module: 'armory',
                    selectedItem: 'armory-shifts',
                    parse: {
                        page: Number,
                        limit: Number,
                        actingBodyId: Number,
                        actingBodyUnitId: Number,
                        beginIn: Number,
                        endIn: Number
                    }
                },
                ArmoryShiftForm: {
                    path: 'armory-shifts/form/:id?',
                    module: 'armory',
                    selectedItem: 'armory-shifts',
                },
                MyArmoryShifts: {
                    path: 'my-armory-shifts',
                    module: 'armory',
                    selectedItem: 'my-armory-shifts',
                }
            },
        },
        Deac: {
            path: '/deac',
            screens: {
                Dashboard: {
                    path: 'dashboard',
                    module: 'deac',
                    selectedItem: 'dashboard',
                    parse: {
                        beginIn: Number,
                        endIn: Number,
                    }
                },
                VacancyLevels: {
                    path: 'vacancy-levels',
                    module: 'deac',
                    selectedItem: 'vacancy-levels',
                    parse: {
                        page: Number,
                        limit: Number,
                        actingBodyId: Number,
                    }
                },
                Workplaces: {
                    path: 'workplaces',
                    module: 'deac',
                    selectedItem: 'workplaces',
                    parse: {
                        page: Number,
                        limit: Number,
                        actingBodyId: Number,
                        actingBodyUnitId: Number,
                        actingBodyCommandId: Number,
                    }
                },
                VacancyTags: {
                    path: 'vacancy-tags',
                    module: 'deac',
                    selectedItem: 'vacancy-tags',
                    parse: {
                        page: Number,
                        limit: Number,
                    }
                },
                VacancyCancellationReasons: {
                    path: 'vacancy-cancellation-reasons',
                    module: 'deac',
                    selectedItem: 'vacancy-cancellation-reasons',
                    parse: {
                        page: Number,
                        limit: Number,
                    }
                },
                Vacancies: {
                    path: 'vacancies',
                    module: 'deac',
                    selectedItem: 'vacancies',
                    parse: {
                        page: Number,
                        limit: Number,
                        actingBodyId: Number,
                        actingBodyUnitId: Number,
                        actingBodyCommandId: Number,
                        status: String,
                        levelsIds: (levels: string) => {
                            return levels.length > 0 ? levels.split(',') : [];
                        },
                        tagsIds: (tags: string) => {
                            return tags.length > 0 ? tags.split(',') : [];
                        },
                        createdBy: Number,
                        beginIn: Number,
                        endIn: Number,
                    }
                },
                VacancyForm: {
                    path: 'vacancies-form/:id?',
                    module: 'deac',
                    selectedItem: 'vacancies'
                },
                PresenceControl: {
                    path: 'presence-control',
                    module: 'deac',
                    selectedItem: 'presence-control',
                    parse: {
                        page: Number,
                        limit: Number,
                        actingBodyId: Number,
                        actingBodyUnitId: Number,
                        vacancyActingBodyId: Number,
                        vacancyActingBodyCommandId: Number,
                        vacancyActingBodyUnitId: Number,
                    }
                },
                DeacReports: {
                    path: 'reports',
                    module: 'deac',
                    selectedItem: 'reports',
                    parse: {
                        page: Number,
                    }
                },
                Applications: {
                    path: 'applications',
                    module: 'deac',
                    selectedItem: 'applications',
                    parse: {
                        page: Number,
                        limit: Number,
                        actingBodyId: Number,
                        actingBodyUnitId: Number,
                        actingBodyCommandId: Number,
                        status: String,
                        userId: Number,
                    }
                },
                VacanciesTemplates: {
                    path: 'vacancies-templates',
                    module: 'deac',
                    selectedItem: 'vacancies-templates',
                    parse: {
                        page: Number,
                        limit: Number,
                        actingBodyId: Number,
                        actingBodyUnitId: Number,
                        actingBodyCommandId: Number,
                        levelsIds: (levels: string) => {
                            return levels.length > 0 ? levels.split(',') : [];
                        },
                        tagsIds: (tags: string) => {
                            return tags.length > 0 ? tags.split(',') : [];
                        },
                    }
                },
                VacancyTemplateForm: {
                    path: 'vacancies-templates-form/:id?',
                    module: 'deac',
                    selectedItem: 'vacancies-templates'
                },
            }
        },
        ThirdPartyApprovedCompanies: {
            path: '/third-party-approved-companies',
            screens: {
                InscriptionsManagement: {
                    path: 'inscriptions-management',
                    module: 'third-party-approved-companies',
                    selectedItem: 'inscriptions-management',
                    parse: createLinkingParse(InscriptionsManagementSchema),
                },
                InscriptionForm: {
                    path: 'inscription-form/:id?',
                    module: 'third-party-approved-companies',
                    selectedItem: 'inscriptions-management',
                    parse: createLinkingParse(InscriptionFormSchema),
                },
                DonatedCamerasList: {
                    path: 'donated-cameras',
                    module: 'third-party-approved-companies',
                    selectedItem: 'donated-cameras',
                    parse: createLinkingParse(DonatedCamerasListSchema),
                },
            }
        },
        Citizen: {
            path: '/citizen',
            screens: {
                CitizenManagement: {
                    path: 'citizen-management',
                    module: 'citizen',
                    selectedItem: 'citizen-management',
                    parse: createLinkingParse(CitizenManagementSchema),
                },
                CitizenLprEvents: {
                    path: 'citizen-lpr-events',
                    module: 'citizen',
                    selectedItem: 'citizen-lpr-events',
                    parse: createLinkingParse(CitizenLprEventsSchema),
                },
                CitizenLprSearch: {
                    path: 'citizen-lpr-search',
                    module: 'citizen',
                    selectedItem: 'citizen-lpr-search',
                    parse: createLinkingParse(CitizenLprSearchSchema),
                },
                CitizenLprEventsDetails: {
                    path: 'citizen-lpr-events/details/:id?',
                    module: 'citizen',
                    selectedItem: 'citizen-lpr-events',
                },
                CitizenBlockReasons: {
                    path: 'citizen-block-reasons',
                    module: 'citizen',
                    selectedItem: 'citizen-block-reasons',
                    parse: createLinkingParse(CitizenBlockReasonsSchema),
                },
            }
        },
    },
};

export const pages: { [screen: string]: { module?: string; selectedItem?: string; }; } = {};


function getNestedScreens(screens: Screens | undefined) {
    if (!screens) {
        return;
    }
    for (const screen of Object.keys(screens)) {
        if (typeof screens[screen] == 'object') {
            const screenObj = screens[screen];
            if (screenObj.module && screenObj.selectedItem) {
                pages[screen] = { module: screenObj.module, selectedItem: screenObj.selectedItem };
                delete screens[screen].module;
                delete screens[screen].selectedItem;
            }
            if (screens[screen].screens && typeof screens[screen].screens !== 'string') {
                getNestedScreens(screens[screen].screens);
            }
        }
    }
}

getNestedScreens(config.screens);

export const linking = {
    prefixes: ['https://sentinelx.com.br'],
    config,
};

interface DefaultParams {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    route: any;
}

const PagesRoutes = ({ route }: DefaultParams) => {

    const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (route?.params?.pathname) {
                const path = route.params.pathname.split('/');

                if (path.length == 3 && path[1] == 'reset-password') {
                    navigation.navigate('ResetPassword');
                }

                if (path.length == 2 && path[1] == 'terms-of-use') {
                    navigation.navigate('TermsOfUse');
                }
            }
            navigation.reset({
                index: 0,
                routes: [{ name: 'Login' }],
            });
        }, 0);

        return () => clearTimeout(timeout);
    }, [navigation, route]);

    return <></>;
};

export function Loader() {
    const { theme } = useStyles();

    return <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
    </View>;
}

export function MySafeAreaProvider(props: {
    children: React.ReactNode | undefined;
    navigationRef: NavigationContainerRefWithCurrent<RootStackParamList>;
}) {
    const { theme } = useStyles();
    const [, updateState] = React.useState({});
    const forceUpdate = React.useCallback(() => updateState({}), []);

    props.navigationRef.addListener('state', () => {
        forceUpdate();
    });

    const pathname = useMemo(() => window.location.pathname, [window.location.pathname]);

    return <SafeAreaProvider
        style={{
            backgroundColor: theme.colors.defaultPageContainer.background
        }}
    >
        <MenuProvider>
            <NavigationContainer ref={props.navigationRef} linking={linking} theme={{
                ...DefaultTheme,
                colors: {
                    ...DefaultTheme.colors,
                    background: theme.colors.defaultPageContainer.background
                }
            }} >
                {pathname.includes('/reset-password/') || pathname.includes('/login') ?
                    props.children
                    : <DefaultPageContainer >
                        <DefaultPageLayout navigationRef={props.navigationRef}>
                            {props.children}
                        </DefaultPageLayout>
                    </DefaultPageContainer>
                }
            </NavigationContainer>
        </MenuProvider>
    </SafeAreaProvider>;
}

export default function MainRoutes() {
    const { isParentApp } = useCurrentApp();

    return (
        // this is the background color for the bottom-bar of the app
        <Stack.Navigator>
            <Stack.Screen name='MainPage' component={PagesRoutes} options={{ headerShown: false }} />
            <Stack.Screen name='ChangeLogs' component={ChangeLogs} options={{ headerShown: false, title: translate('changeLogs') }} />
            <Stack.Screen name='ContactUs' component={ContactUs} options={{ headerShown: false, title: translate('contactUs') }} />
            <Stack.Screen name='FrequentlyAskedQuestions' component={FrequentlyAskedQuestions} options={{ headerShown: false, title: 'FAQ' }} />
            <Stack.Screen name='PublicFrequentlyAskedQuestions' component={PublicFrequentlyAskedQuestions} options={{ headerShown: false, title: 'FAQ' }} />
            <Stack.Screen name='TermsOfUse' component={TermsOfUse} options={{ headerShown: false, title: translate('termsOfUse') }} />
            <Stack.Screen name='ResetPassword' component={ResetPassword} options={{ headerShown: false, title: translate('resetPassword') }} />
            <Stack.Screen name='Login' component={Login} options={{ headerShown: false, title: translate('login') }} />
            <Stack.Screen name='Menu' component={Menu} options={{ headerShown: false, title: translate('menu') }} />
            <Stack.Screen name='Dispatch' options={{ headerShown: false }}>
                {() => (
                    <DispatchStack.Navigator initialRouteName='Resume'>
                        <DispatchStack.Screen name='Resume' component={Resume} options={{ headerShown: false, title: translate('dispatch') + ' ' + translate('resume') }} />
                        <DispatchStack.Screen name='Occurrences' component={Occurrences} initialParams={{
                            page: 1,
                            beginIn: moment().subtract(1, 'week').toDate().getTime(),
                        }} options={{ headerShown: false, title: translate('occurrences') }} />
                        <DispatchStack.Screen name='PoliceStations' component={PoliceStations} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('policeStations') }} />
                        <DispatchStack.Screen name='CityProperties' component={CityProperties} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('cityProperties') }} />
                        <DispatchStack.Screen name='CityPropertyForm' component={CityPropertyForm} options={{ headerShown: false, title: translate('cityProperties') }} />
                        <DispatchStack.Screen name='HandcuffReasons' component={HandcuffReasons} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('handcuffReasons') }} />
                        <DispatchStack.Screen name='Providences' component={Providences} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('providences') }} />
                        <DispatchStack.Screen name='PoliceStationForm' component={PoliceStationForm} options={{ headerShown: false, title: translate('policeStations') }} />
                        <DispatchStack.Screen name='DispatchOccurrenceDetail' component={DispatchOccurrenceDetail} options={{ headerShown: false, title: translate('occurrence') }} />
                        <DispatchStack.Screen name='OccurrenceTypes' component={OccurrenceTypes} initialParams={{ page: 1, isActive: true }} options={{ headerShown: false, title: translate('occurrence_types') }} />
                        <DispatchStack.Screen name='Equipments' component={Equipment} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('equipments') }} />
                        <DispatchStack.Screen name='VehicleRelations' component={VehicleRelations} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('vehicleRelations') }} />
                        <DispatchStack.Screen name='PersonRelations' component={PersonRelations} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('personRelations') }} />
                        <DispatchStack.Screen name='DispatchReports' component={DispatchReports} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('reports') }} />
                        <DispatchStack.Screen name='Units' component={Units} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('teams') }} />
                        <DispatchStack.Screen name='UnitDisplacedReasons' component={UnitDisplacedReasons} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('unitDisplacedReasons') }} />
                        <DispatchStack.Screen name='UnitForm' component={UnitForm} options={{ headerShown: false, title: translate('team') }} />
                        <DispatchStack.Screen name='ObjectsCategories' component={ObjectsCategories} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('objectsCategories') }} />
                        <DispatchStack.Screen name='FinishCategories' component={FinishCategories} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('finishCategories') }} />
                        <DispatchStack.Screen name='Assignments' component={Assignments} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('assignments') }} />
                        <DispatchStack.Screen name='OccurrenceTags' component={OccurrenceTags} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('occurrenceTags') }} />
                        <DispatchStack.Screen name='ReportTags' component={DispatchReportTags} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('reportTags') }} />
                        <DispatchStack.Screen name='TeamNames' component={TeamNames} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('teamNames') }} />
                        <DispatchStack.Screen name='TeamName' component={TeamName} options={{ headerShown: false, title: translate('teamNames') }} />
                        <DispatchStack.Screen name='TeamPrograms' component={TeamPrograms} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('teamPrograms') }} />
                        <DispatchStack.Screen name='UnitPauseReasons' component={UnitPauseReasons} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('unitPauseReasons') }} />
                        <DispatchStack.Screen name='UnitFinishEarlyReasons' component={UnitFinishEarlyReasons} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('unitFinishEarlyReasons') }} />
                    </DispatchStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='CameraMapPlayer' component={CameraPlayer} options={{ headerShown: false, title: translate('camera') }} />
            <Stack.Screen name='CameraPlayer' component={CameraPlayer} options={{ headerShown: false, title: translate('camera') }} />
            {isParentApp &&
                <Stack.Screen name='WebRTC' options={{ headerShown: false }}>
                    {() => (
                        <WebRtcStack.Navigator initialRouteName='Publisher'>
                            <WebRtcStack.Screen name='Publisher' component={Publisher} options={{ headerShown: false, title: translate('publisher') }} />
                            <WebRtcStack.Screen name='Consumer' component={Consumer} options={{ headerShown: false, title: translate('consumer') }} />
                        </WebRtcStack.Navigator >
                    )}
                </Stack.Screen>
            }
            {isParentApp &&
                <Stack.Screen name='StreamServers' options={{ headerShown: false }}>
                    {() => (
                        <StreamServersStack.Navigator initialRouteName='ServerFarms'>
                            <StreamServersStack.Screen name='ServerFarms' component={StreamServers} options={{ headerShown: false, title: translate('streamServers') }} />
                            <StreamServersStack.Screen name='ServerFarmDetail' component={ServerFarmDetail} options={{ headerShown: false, title: translate('serverFarmDetail') }} />
                        </StreamServersStack.Navigator>
                    )}
                </Stack.Screen>
            }
            <Stack.Screen name='Cameras' options={{ headerShown: false }}>
                {() => (
                    <CamerasStack.Navigator initialRouteName='CamerasList'>
                        <CamerasStack.Screen name='CamerasMap' component={CamerasMap} options={{ headerShown: false, title: translate('map') }} />
                        <CamerasStack.Screen name='Mosaic' component={Mosaic} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('mosaic') }} />
                        <CamerasStack.Screen name='CamerasList' component={CamerasList} initialParams={{ page: 1, online: true }} options={{ headerShown: false, title: translate('cameras') }} />
                        <CamerasStack.Screen name='CameraDetails' component={CameraDetails} options={{ headerShown: false, title: translate('camera') }} />
                        <CamerasStack.Screen name='MosaicDetail' component={MosaicDetail} options={{ headerShown: false, title: translate('mosaic') }} />
                        <CamerasStack.Screen name='CameraTags' component={CameraTags} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('tags') }} />
                        <CamerasStack.Screen name='Downloads' component={Downloads} initialParams={{ page: 1 }} options={{ headerShown: false, title: 'Downloads' }} />
                        <CamerasStack.Screen name='DownloadTags' component={DownloadTags} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('downloadsTags') }} />
                        <CamerasStack.Screen name='VideoAnalysis' component={VideoAnalysis} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('videoAnalysis') }} />
                        <CamerasStack.Screen name='VideoAnalysisDetails' component={VideoAnalysisDetails} options={{ headerShown: false, title: translate('videoAnalysis') }} />
                        <CamerasStack.Screen name='AlertGroups' component={AlertGroups} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('alertGroups') }} />
                        <CamerasStack.Screen name='CamerasReports' component={CamerasReports} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('reports') }} />
                    </CamerasStack.Navigator >
                )
                }
            </Stack.Screen >
            <Stack.Screen name='LiveEventDetail' component={LiveEventDetail} options={{ headerShown: false, title: translate('detection') }} />
            <Stack.Screen name='LiveAlertEventDetail' component={LiveAlertEventDetail} options={{ headerShown: false, title: translate('detection') }} />
            <Stack.Screen name='SearchEventDetail' component={SearchEventDetail} options={{ headerShown: false, title: translate('detection') }} />
            <Stack.Screen name='Facial' options={{ headerShown: false }}>
                {() => (
                    <FacialStack.Navigator initialRouteName='LiveEvents'>
                        <FacialStack.Screen name='LiveEvents' initialParams={{}} component={LiveEvents} options={{ headerShown: false, title: translate('liveEvents') }} />
                        <FacialStack.Screen name='LiveAlerts' initialParams={{}} component={LiveAlerts} options={{ headerShown: false, title: translate('liveAlerts') }} />
                        <FacialStack.Screen name='Search' component={Search} initialParams={{
                            onlyWithPerson: true,
                            endIn: moment().toDate().getTime(),
                        }} options={{ headerShown: false, title: translate('search') }} />
                        <FacialStack.Screen name='Markers' initialParams={{ page: 0 }} component={Markers} options={{ headerShown: false, title: translate('tags') }} />
                        <FacialStack.Screen name='PeopleList' component={PeopleList} initialParams={{ page: 1, onlyActive: true }} options={{ headerShown: false, title: translate('registerPerson') }} />
                        <FacialStack.Screen name='PersonForm' component={PersonForm} options={{ headerShown: false, title: translate('registerPerson') }} />
                        <FacialStack.Screen name='IgnoredPeople' initialParams={{ page: 0 }} component={IgnoredPeople} options={{ headerShown: false, title: translate('ignoredList') }} />
                    </FacialStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='SmartSearchFacial' options={{ headerShown: false }}>
                {() => (
                    <SmartSearchFacialStack.Navigator initialRouteName='SmartSearchFacial'>
                        <SmartSearchFacialStack.Screen name='SmartSearchFacial' component={SmartSearchFacial} options={{ headerShown: false, title: translate('smartSearchFacial') }} />
                    </SmartSearchFacialStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='User' options={{ headerShown: false }}>
                {() => (
                    <UserStack.Navigator initialRouteName='Account'>
                        <UserStack.Screen name='Account' component={Account} options={{ headerShown: false, title: translate('account') }} />
                        <UserStack.Screen name='Password' component={Password} options={{ headerShown: false, title: translate('changePassword') }} />
                        <UserStack.Screen name='Sessions' component={Sessions} options={{ headerShown: false, title: translate('sessions') }} />
                        <UserStack.Screen name='Calendar' component={Calendar} options={{ headerShown: false, title: translate('calendar') }} />
                        <UserStack.Screen name='OwnSupportTickets' component={OwnSupportTickets} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('myTicket') }} />
                        <UserStack.Screen name='GetOwnSupportTicket' component={GetOwnSupportTicket} options={{ headerShown: false, title: translate('myTicket') }} />
                        <UserStack.Screen name='Notes' component={Notes} options={{ headerShown: false, title: translate('notes') }} />
                    </UserStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='ClonedPlate' options={{ headerShown: false }}>
                {() => (
                    <ClonedPlateStack.Navigator initialRouteName='ClonedPlates'>
                        <ClonedPlateStack.Screen name='ClonedPlates' component={ClonedPlates} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('clonedPlates') }} />
                        <ClonedPlateStack.Screen name='CloneSuspicions' component={CloneSuspicions} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('cloneSuspicions') }} />
                    </ClonedPlateStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='SearchLprDetection' component={SearchLprDetection} options={{ headerShown: false, title: translate('detection') }} />
            <Stack.Screen name='LiveAlertLprDetection' component={LiveAlertLprDetection} options={{ headerShown: false, title: translate('detection') }} />
            <Stack.Screen name='LiveEventLprDetection' component={LiveEventLprDetection} options={{ headerShown: false, title: translate('detection') }} />
            <Stack.Screen name='LPR' options={{ headerShown: false }}>
                {() => (
                    <LprStack.Navigator initialRouteName='LprLiveEvents'>
                        <LprStack.Screen name='LprLiveEvents' initialParams={{}} component={LprLiveEvents} options={{ headerShown: false, title: translate('liveEvents') }} />
                        <LprStack.Screen name='LprLiveAlerts' initialParams={{}} component={LprLiveAlerts} options={{ headerShown: false, title: translate('liveAlerts') }} />
                        <LprStack.Screen name='LprSearch' component={LprSearch} initialParams={{
                            endIn: moment().toDate().getTime(),
                        }} options={{ headerShown: false, title: translate('search') }} />
                        <LprStack.Screen name='Plates' component={Plates} initialParams={{ page: 1, onlyActive: true }} options={{ headerShown: false, title: translate('plates') }} />
                        <LprStack.Screen name='LprReports' component={LprReports} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('reports') }} />
                    </LprStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='GuardianApp' options={{ headerShown: false }}>
                {() => (
                    <GuardianAppStack.Navigator initialRouteName='GuardianAppUsers'>
                        <GuardianAppStack.Screen name='GuardianAppUsers' component={GuardianAppUsers} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('guardianApp') }} />
                    </GuardianAppStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='Access' options={{ headerShown: false }}>
                {() => (
                    <AccessStack.Navigator initialRouteName='Users'>
                        <AccessStack.Screen name='Users' component={Users} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('users') }} />
                        <AccessStack.Screen name='UserForm' component={UserForm} options={{ headerShown: false, title: translate('users') }} />
                        <AccessStack.Screen name='UserTags' component={UserTags} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('userTags') }} />
                        <AccessStack.Screen name='PermissionLevels' component={PermissionLevels} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('permissionLevels') }} />
                        <AccessStack.Screen name='PermissionLevelForm' component={PermissionLevelForm} options={{ headerShown: false, title: translate('permissionLevels') }} />
                        <AccessStack.Screen name='Turnstiles' component={Turnstiles} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('turnstiles') }} />
                        <AccessStack.Screen name='Groups' component={Groups} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('groups') }} />
                        <AccessStack.Screen name='GroupForm' component={GroupForm} options={{ headerShown: false, title: translate('groups') }} />
                        <AccessStack.Screen name='ActingBodies' component={ActingBodies} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('departments') }} />
                        <AccessStack.Screen name='ActingBodiesForm' component={ActingBodiesForm} options={{ headerShown: false, title: translate('departments') }} />
                        <AccessStack.Screen name='ActingBodyUnits' component={ActingBodyUnits} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('actingBodyUnits') }} />
                        <AccessStack.Screen name='ActingBodyCommands' component={ActingBodyCommands} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('commands') }} />
                        <AccessStack.Screen name='Roles' component={Roles} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('roles') }} />
                        <AccessStack.Screen name='InstallationCompanies' component={InstallationCompanies} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('installationCompanies') }} />
                        <AccessStack.Screen name='InternetCompanies' component={InternetCompanies} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('internetCompanies') }} />
                        <AccessStack.Screen name='PlatformSettings' component={PlatformSettings} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('platformSettings') }} />
                        <AccessStack.Screen name='PtzPriority' component={PtzPriority} options={{ headerShown: false, title: translate('ptzPriority') }} />
                        <AccessStack.Screen name='AllowedIps' component={AllowedIps} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('allowedIps') }} />
                        <AccessStack.Screen name='AbuseReports' component={AbuseReports} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('abuseReports') }} />
                        <AccessStack.Screen name='WorkSchedules' component={WorkSchedules} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('workSchedules') }} />
                        <AccessStack.Screen name='Skills' component={Skills} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('skills') }} />
                        <AccessStack.Screen name='Holidays' component={Holidays} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('holidays') }} />
                        <AccessStack.Screen name='SupportTickets' component={SupportTickets} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('supportTickets') }} />
                        <AccessStack.Screen name='SupportTicketDetail' component={SupportTicketDetail} options={{ headerShown: false, title: translate('supportTickets') }} />
                    </AccessStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='Events' options={{ headerShown: false }}>
                {() => (
                    <EventStack.Navigator initialRouteName='EventsBoard'>
                        <EventStack.Screen name='EventsBoard' initialParams={{ beginIn: moment().subtract(12, 'hours').valueOf() }} component={EventsBoard} options={{ headerShown: false, title: translate('events') }} />
                        <EventStack.Screen name='EventsOccurrenceDetail' component={EventsOccurrenceDetail} options={{ headerShown: false, title: translate('occurrences') }} />
                    </EventStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='AppAgent' options={{ headerShown: false }}>
                {() => (
                    <AppAgentStack.Navigator initialRouteName='AgentOccurrences'>
                        <AppAgentStack.Screen name='AgentOccurrences' component={AgentOccurrences} options={{ headerShown: false, title: translate('occurrences') }} />
                        <AppAgentStack.Screen name='AgentOccurrenceDetails' component={AgentOccurrenceDetails} options={{ headerShown: false, title: translate('occurrences') }} />
                        {(window.location.hostname.includes('parapicuiba') || isParentApp) &&
                            <AppAgentStack.Screen name='PersonOfInterest' component={PersonOfInterest} options={{ headerShown: false, title: translate('personOfInterest') }} />
                        }
                        <AppAgentStack.Screen name='AgentUnits' component={AgentUnits} options={{ headerShown: false, title: translate('teams') }} />
                        <AppAgentStack.Screen name='CortexFinder' component={CortexFinder} options={{ headerShown: false, title: 'Cortex' }} />
                        <AppAgentStack.Screen name='AgentSignatures' component={AgentSignatures} options={{ headerShown: false, title: translate('signatures') }} />
                        <AppAgentStack.Screen name='OpenVacancies' component={OpenVacancies} initialParams={{ showOnlyAvailableVacancies: true }} options={{ headerShown: false, title: translate('openVacancies') }} />
                        <AppAgentStack.Screen name='VacancyDetailsApply' component={VacancyDetailsApply} options={{ headerShown: false, title: translate('vacancyDetails') }} />
                        <AppAgentStack.Screen name='VacancyDetailsCancel' component={VacancyDetailsCancel} options={{ headerShown: false, title: translate('vacancyDetails') }} />
                        <AppAgentStack.Screen name='Debugger' component={Debugger} initialParams={{ tags: [] }} options={{ headerShown: false, title: translate('debugger') }} />
                        <AppAgentStack.Screen name='Services' component={Services} options={{ headerShown: false, title: translate('services') }} />
                        <AppAgentStack.Screen name='Candidacies' component={Candidacies} initialParams={{ beginIn: moment().startOf('year').valueOf(), endIn: moment().endOf('year').valueOf() }} options={{ headerShown: false, title: translate('myCandidacies') }} />
                        <AppAgentStack.Screen name='LprEvents' component={LprEvents} options={{ headerShown: false, title: translate('lprEvents') }} />
                    </AppAgentStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='Dashboard' options={{ headerShown: false }}>
                {() => (
                    <DashboardStack.Navigator initialRouteName='DashboardOccurrences'>
                        <DashboardStack.Screen name='DashboardCameras' component={DashboardCameras} options={{ headerShown: false, title: translate('dashboard') }} />
                        <DashboardStack.Screen name='DashboardOccurrences' initialParams={{ page: 1, beginIn: moment().subtract(12, 'hours').valueOf() }} component={DashboardOccurrences} options={{ headerShown: false, title: translate('dashboard') }} />
                        <DashboardStack.Screen name='DashboardAgents' component={DashboardAgents} options={{ headerShown: false, title: translate('agents') }} />
                        <DashboardStack.Screen name='DashboardVehicles' component={DashboardVehicles} options={{ headerShown: false, title: translate('vehicles') }} />
                        <DashboardStack.Screen name='DashboardPlateMonitoring' component={DashboardPlateMonitoring} options={{ headerShown: false, title: translate('plateMonitoring') }} />
                    </DashboardStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='ManagementDetails' component={ManagementDetails} options={{ headerShown: false, title: translate('cameras') }} />
            <Stack.Screen name='AlarmCenterDetails' component={AlarmCenterDetails} options={{ headerShown: false, title: translate('alarmCenters') }} />
            <Stack.Screen name='CRM' options={{ headerShown: false }}>
                {() => (
                    <CrmStack.Navigator initialRouteName='Management'>
                        <CrmStack.Screen name='CameraHistory' component={CameraHistory} initialParams={{ page: 1, orderBy: 'start', orderDirection: 'asc' }} options={{ headerShown: false, title: translate('cameraHistory') }} />
                        <CrmStack.Screen name='Management' component={Management} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('cameras') }} />
                        <CrmStack.Screen name='AlarmCentersList' component={AlarmCentersList} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('alarmCenters') }} />
                        <CrmStack.Screen name='TicketReasons' component={TicketReasons} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('drpScenarios') }} />
                        <CrmStack.Screen name='OfflineEvents' component={CameraHistory} initialParams={{ page: 1, orderBy: 'start', orderDirection: 'asc' }} options={{ headerShown: false, title: translate('offlineEvents') }} />
                        {isParentApp &&
                            <>
                                <CrmStack.Screen name='KanbanTicketDetail' component={TicketDetail} options={{ headerShown: false, title: translate('ticketDetail') }} />
                                <CrmStack.Screen name='OfflineEventsKanban' component={OfflineEventsKanban} options={{ headerShown: false, title: translate('offlineEventsKanban') }} />
                            </>
                        }
                        <CrmStack.Screen name='MaintenanceRequests' component={MaintenanceRequests} options={{ headerShown: false, title: translate('maintenanceRequests') }} />
                        <CrmStack.Screen name='TicketDetail' component={TicketDetail} options={{ headerShown: false, title: translate('ticketDetail') }} />
                        <CrmStack.Screen name='TicketCameraDetail' component={TicketDetail} options={{ headerShown: false, title: translate('ticketDetail') }} />
                    </CrmStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='Logs' options={{ headerShown: false }}>
                {() => (
                    <LogsStack.Navigator initialRouteName='LogList'>
                        <LogsStack.Screen name='LogList' component={LogList} initialParams={{ dateLte: moment().endOf('day').valueOf(), dateGte: moment().startOf('day').valueOf() }} options={{ headerShown: false, title: translate('logs') }} />
                        <LogsStack.Screen name='LogReports' component={LogReports} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('reports') }} />
                    </LogsStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='Signatures' options={{ headerShown: false }}>
                {() => (
                    <SignaturesStack.Navigator initialRouteName='Signatures'>
                        <SignaturesStack.Screen name='Signatures' component={Signatures} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('signatures') }} />
                    </SignaturesStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name='Armory' options={{ headerShown: false }}>
                {() => (
                    <ArmoryStack.Navigator initialRouteName='ArmoryShifts'>
                        <ArmoryStack.Screen name='ArmoryShifts' component={ArmoryShifts} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('manageShifts') }} />
                        <ArmoryStack.Screen name='ArmoryShiftForm' component={ArmoryShiftForm} options={{ headerShown: false, title: translate('manageShifts') }} />
                        <ArmoryStack.Screen name='MyArmoryShifts' component={MyArmoryShifts} options={{ headerShown: false, title: translate('myShifts') }} />
                    </ArmoryStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name="Deac" options={{ headerShown: false }}>
                {() => (
                    <DeacStack.Navigator initialRouteName='Vacancies'>
                        <DeacStack.Screen name='Vacancies' component={Vacancies} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('vacanciesManagement') }} />
                        <DeacStack.Screen name='VacancyForm' component={VacancyForm} options={{ headerShown: false, title: translate('vacanciesManagement') }} />
                        <DeacStack.Screen name='VacancyLevels' component={VacancyLevels} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('vacancyLevels') }} />
                        <DeacStack.Screen name='Workplaces' component={Workplaces} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('workplaces') }} />
                        <DeacStack.Screen name='VacancyTags' component={VacancyTags} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('vacancyTags') }} />
                        <DeacStack.Screen name='VacancyCancellationReasons' component={VacancyCancellationReasons} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('vacancyCancellationReasons') }} />
                        <DeacStack.Screen name='PresenceControl' component={PresenceControl} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('presenceControl') }} />
                        <DeacStack.Screen name='DeacReports' component={DeacReports} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('reports') }} />
                        <DeacStack.Screen name='Dashboard' component={Dashboard} initialParams={{ beginIn: moment().startOf('day').subtract(30, 'days').valueOf(), endIn: moment().endOf('day').valueOf() }} options={{ headerShown: false, title: translate('presenceControl') }} />
                        <DeacStack.Screen name='Applications' component={Applications} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('candidacies') }} />
                        <DeacStack.Screen name='VacanciesTemplates' component={VacanciesTemplates} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('vacanciesTemplates') }} />
                        <DeacStack.Screen name='VacancyTemplateForm' component={VacancyTemplateForm} options={{ headerShown: false, title: translate('vacanciesTemplates') }} />
                    </DeacStack.Navigator>
                )}
            </Stack.Screen>
            <Stack.Screen name="ThirdPartyApprovedCompanies" options={{ headerShown: false }}>
                {() => (
                    <ThirdPartyApprovedCompaniesStack.Navigator initialRouteName='InscriptionsManagement'>
                        <ThirdPartyApprovedCompaniesStack.Screen name='InscriptionsManagement' component={Inscriptions} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('inscriptionsManagement') }} />
                        <ThirdPartyApprovedCompaniesStack.Screen name='InscriptionForm' component={InscriptionForm} options={{ headerShown: false, title: translate('inscriptionsManagement') }} />
                        <ThirdPartyApprovedCompaniesStack.Screen name='DonatedCamerasList' component={DonatedCamerasList} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('cameras') }} />
                    </ThirdPartyApprovedCompaniesStack.Navigator>
                )}
            </Stack.Screen>

            <Stack.Screen name="Citizen" options={{ headerShown: false }}>
                {() => (
                    <CitizenStack.Navigator initialRouteName='CitizenManagement'>
                        <CitizenStack.Screen name='CitizenManagement' component={CitizenManagement} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('citizenManagement') }} />
                        <CitizenStack.Screen name='CitizenLprEvents' component={CitizenLprEvents} options={{ headerShown: false, title: translate('citizenLprEvents') }} />
                        <CitizenStack.Screen name='CitizenLprSearch' component={CitizenLprSearch} initialParams={{ endIn: moment().valueOf() }} options={{ headerShown: false, title: translate('plateSearch') }} />
                        <CitizenStack.Screen name='CitizenLprEventsDetails' component={CitizenLprEventsDetails} options={{ headerShown: false, title: translate('citizenLprEvents') }} />
                        <CitizenStack.Screen name='CitizenBlockReasons' component={CitizenBlockReasons} initialParams={{ page: 1 }} options={{ headerShown: false, title: translate('citizenBlockReasons') }} />
                    </CitizenStack.Navigator>
                )}
            </Stack.Screen>
        </Stack.Navigator >
    );
}
